/**
 * The client's settings, and **the one place that owns them** (T187).
 *
 * ## 🔴 Why this file exists at all
 *
 * Until T187 the light settings lived inside `devtools.ts`, which was fine
 * while the Dev Tools window was the only thing that could change them. The
 * game menu (`gamemenu.ts`) now carries the four *Look at* buttons, while the
 * sun and sky **sliders** stayed in Dev Tools — so **two windows change one
 * object.**
 *
 * 🔴 **That is the fault this repository has recorded five times**: the hearth,
 * the driftwood, the pointer lock, breaking-vs-opening a house, and a camera
 * fix written into the wrong one of two functions that both place the camera.
 * *A second way in forgets what the first one does.* Two copies of
 * `LightSettings` would mean pressing **Norðurljós** in the menu and then
 * opening Dev Tools to find its own buttons still saying the world is ordinary
 * — and each window would overwrite the other on its next save.
 *
 * ✅ **So there is one value, one save, and every window subscribes.** Nothing
 * outside this file reads `localStorage` for a setting, and nothing outside it
 * holds a setting that anybody else can change.
 *
 * ## ⚠️ A subscriber is called immediately, on purpose
 *
 * {@link Store.subscribe} runs the listener once with the current value before
 * returning. That is not a convenience — it is a bug fix kept as a rule.
 * haugur reported from a screenshot on 2026-08-11 that a Dev Tools switch sat
 * on screen as **a blank bar**: its label was only ever written by the "a
 * setting changed" path, so on a freshly opened panel it had no text at all — a
 * control nobody could see, name or discover. **A window that renders itself
 * only on change is wrong on the frame it opens.**
 */

import { DEFAULT_VIEW_RADIUS, clampViewRadius } from './streaming.ts';

/** Which tone-mapping curve the renderer applies. */
export type ToneMappingName = 'none' | 'neutral' | 'aces' | 'agx';

export const TONE_MAPPINGS: readonly { name: ToneMappingName; label: string }[] = [
  { name: 'none', label: 'None' },
  { name: 'neutral', label: 'Neutral' },
  { name: 'aces', label: 'ACES' },
  { name: 'agx', label: 'AgX' },
];

/**
 * Everything about the light that is a judgement call rather than a fact.
 *
 * ⚠️ **These are sliders and not constants for a reason.** This host has no
 * GPU, the browser preview crashes on the game, and haugur is the only person
 * who can actually see the client. A lighting number chosen by arithmetic and
 * never looked at is exactly the kind of confident mistake this project has no
 * second engineer to catch — so the same argument that made render distance
 * adjustable at runtime (see `main.ts`) applies here, more strongly.
 */
export interface LightSettings {
  /** Directional light intensity. */
  readonly sun: number;
  /**
   * Sky-light intensity — the **hemisphere** light, not a flat ambient one.
   *
   * ⚠️ Named `ambient` because that is what it started as. It now drives a
   * `HemisphereLight`, which lights a surface according to which way it faces:
   * sky colour from above, ground bounce from below. That is the fix for walls
   * facing away from the sun rendering as black cut-outs — outdoors they are
   * lit by the whole sky, and a flat ambient term cannot express that.
   */
  readonly ambient: number;
  /**
   * Sun height above the horizon, in degrees.
   *
   * ⚠️ **Hafnarfjörður is 64.07°N, so the sun there never exceeds 49.4°** —
   * that is solstice noon — and sits at 2–3° at midwinter. The slider goes
   * higher than that only because the shipped value is **64.7°**, which is
   * impossible on any day of any year. Being able to drag from an impossible
   * sun down to a real one is the quickest way to see what it was costing.
   */
  readonly elevation: number;
  /**
   * Sun bearing, degrees clockwise from north.
   *
   * North is **−z** (`northingToZ` is `-northing`), so a bearing `a` points
   * along `(sin a, −cos a)`. Iceland's midday sun is due south, 180°.
   */
  readonly azimuth: number;
  readonly toneMapping: ToneMappingName;
  readonly exposure: number;
  /** Sky colour. Also drives the fog, so the horizon stops reading as a cut-out. */
  readonly sky: string;
  /**
   * Let the **world clock** place the sun and colour the sky (T103 piece ②).
   *
   * 🔴 **On by default, because it is the feature.** Off hands `elevation`,
   * `azimuth` and `sky` back to the three sliders, frozen wherever they were —
   * which is how haugur tuned this light in the first place and how anybody
   * tunes it again. ⚠️ **Turning it off does not stop the world's clock**; it
   * stops *this client* reading it, which is the same shape Blíða will need
   * (D44).
   */
  readonly followClock: boolean;
  /**
   * 🌕 **How dark a moonless night is allowed to get** (T116, **D60**).
   *
   * **0 means dark means dark** — haugur's answer when asked directly — and that
   * is the shipped default. This slider exists because the right value is a
   * judgement made by standing in it on a winter night, not one computed here,
   * and because **there is nothing to carry yet**: fire arrives with T110.
   *
   * ⚠️ **It replaced a hard-coded `NIGHT_SKY_FLOOR = 0.12`** that had a stated
   * expiry — *"when light sources exist, bring it down"*. The moon is that light
   * source, so the constant became a setting rather than simply vanishing.
   */
  readonly nightFloor: number;
  /**
   * 🌟 **Ignore the weather's cloud and pretend the sky is clear** (T116).
   *
   * 🔴 **Without this the new sky is unreviewable.** **D59** hides the stars, the
   * sun and the moon behind cloud — correctly; you cannot see the sun through
   * eight oktas of Icelandic stratus — and dev's archived weather is overcast
   * most of the time, because Iceland is. So the honest behaviour and "let me
   * look at the thing I just built" are in direct conflict, and this is the way
   * out.
   *
   * ⚠️ **A viewing aid, not a game setting.** It changes nothing on the server
   * and nothing for anybody else; it only stops this client believing the cloud.
   */
  readonly clearSky: boolean;
  /**
   * 🌧️ **Make it rain, whatever the archive says** (2026-09-01, haugur's ask:
   * *"first give me a button in dev tools to make it rain"*).
   *
   * 🔴 **Rain is REAL WEATHER, per zone, per day**, so seeing it meant finding a
   * day it happened to be raining on and standing in the right part of Iceland.
   * **A thing you cannot summon is a thing you cannot judge.**
   *
   * ⚠️ **A viewing aid, not a game setting** — the same as {@link clearSky}. It
   * changes nothing on the server and nothing for anybody else, and it does NOT
   * make anybody wet: the cold is charged from the archive on the server, which
   * has never heard of this switch.
   *
   * ⚠️ **`clearSky` wins.** Pretending the sky is clear and forcing rain at once
   * is a contradiction, and the honest answer to it is the clear sky — that
   * switch exists to look at the sky, and rain in front of it defeats the point.
   */
  readonly forceRain: boolean;
  /**
   * 🖐️ **Pretend to be holding this item, so it can be LOOKED AT.**
   * `null` shows whatever is really in the selected slot.
   *
   * 🔴 **This exists because there was no way to see the held item at all.**
   * Dev has no accounts, an anonymous pack lives in server memory, and
   * `shoot.mjs` therefore photographed an empty hand every time — which is how
   * a held tool that looked like a mallet, and later one that did not render at
   * all, both reached haugur before anybody had seen them.
   *
   * ⚠️ **A viewing aid, and it must stay one — the same rule as {@link
   * LightSettings.forceRain}.** It changes ONLY what is drawn. It does not
   * change what you are holding, how fast a block breaks, or whether a tool
   * wears out: those read the real slot on the server, which has never heard of
   * this switch. 🔴 **The moment a debug toggle has a gameplay effect, nobody
   * can trust a bug report made with it on.**
   */
  readonly holdItem: string | null;
  /**
   * 🎬 **One-click scenes** (2026-08-11, haugur's ask).
   *
   * `'none'` is the world as it really is. The others **override the sun, the
   * weather and — at night — the moon**, so a scene always looks like the scene
   * regardless of what day the world happens to be on.
   *
   * 🌌 **`'aurora'` was added on 2026-08-12 at haugur's ask** — *"so i can go
   * there click it and see best case scenarion for auroras."* It poses **Kp 7, a
   * clear sky, the sun at −25° and the moon BELOW THE HORIZON**, because a
   * gibbous moon washes out norðurljós exactly as it washes out the stars.
   * ⚠️ **Kp 9 was rejected**: it is the top of the scale and appears four times
   * in thirty years of the archive, so a "best case" built on it would be a
   * promise the game never keeps.
   *
   * ⚠️ **An overlay, not an edit.** Nothing else in this object changes, so
   * switching a preset off puts you back exactly where you were: the clock's sun
   * and the archive's weather.
   */
  readonly preset: 'none' | 'day' | 'night' | 'aurora';
}

/**
 * **haugur's numbers, found by dragging the sliders on 2026-08-07.**
 *
 * Not derived, not calculated — chosen by the only person who can see the
 * client, which is the entire reason the panel exists. He described them as
 * *"the most comfortable at the moment"*.
 *
 * ⚠️ **Provisional, and deliberately so.** His own qualification, and it is the
 * right one: *"bear in mind world is pretty empty as is… maybe tweak later when
 * we have terrain and a house."* Light is judged against what it falls on, and
 * right now it falls on an unbroken field of birkiskógur with nothing standing
 * in it but the móberg letters he built. The moment there is a house casting a
 * shadow into a doorway, these want revisiting — **so treat them as a starting
 * point, not a settled answer.**
 *
 * Two notes on what the numbers mean:
 *
 * - **`elevation: 31`** is a real Icelandic sun. Hafnarfjörður never exceeds
 *   49.4°, and the value this shipped with was **64.7°** — higher than the sun
 *   can reach there on any day of any year.
 * - **`sun` is well above `ambient`** because the sky light is a
 *   `HemisphereLight` now: it fills the shadowed faces on its own, so the
 *   directional light no longer has to be propped up by a flat ambient term
 *   that washed everything out.
 */
export const DEFAULT_LIGHT: LightSettings = {
  sun: 5.2,
  ambient: 1.9,
  elevation: 31,
  azimuth: 167,
  toneMapping: 'neutral',
  exposure: 1.3,
  sky: '#9FB4C7',
  followClock: true,
  /* 🔴 Zero: dark means dark (D60). Raise it only to see what a floor costs. */
  nightFloor: 0,
  /* Off: the real weather decides what you can see. */
  clearSky: false,
  forceRain: false,
  /* Show what is really in the slot. */
  holdItem: null,
  /* The world as it really is. */
  preset: 'none',
};

/**
 * ⚠️ Version the key, and bump it when the rig changes meaning.
 *
 * `ambient` used to drive an `AmbientLight` and now drives a `HemisphereLight`.
 * A stored 1.45 from the old rig is not the same quantity as 1.45 in the new
 * one, so restoring it would quietly reproduce the too-dark world the change
 * exists to fix — and it would look exactly like the fix not working.
 */
const LIGHT_KEY = 'lofi.light.v2';

/** Read back the last settings, so a reload does not throw away a good result. */
function loadLight(): LightSettings {
  try {
    const raw = window.localStorage.getItem(LIGHT_KEY);
    if (raw === null) return DEFAULT_LIGHT;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_LIGHT;
    /* Field by field, defaulting anything missing or the wrong type. A stored
       shape from an older build must not be able to produce a black screen. */
    const p = parsed as Partial<Record<keyof LightSettings, unknown>>;
    const num = (v: unknown, fallback: number): number =>
      typeof v === 'number' && Number.isFinite(v) ? v : fallback;
    return {
      sun: num(p.sun, DEFAULT_LIGHT.sun),
      ambient: num(p.ambient, DEFAULT_LIGHT.ambient),
      elevation: num(p.elevation, DEFAULT_LIGHT.elevation),
      azimuth: num(p.azimuth, DEFAULT_LIGHT.azimuth),
      toneMapping: TONE_MAPPINGS.some((t) => t.name === p.toneMapping)
        ? (p.toneMapping as ToneMappingName)
        : DEFAULT_LIGHT.toneMapping,
      exposure: num(p.exposure, DEFAULT_LIGHT.exposure),
      sky: typeof p.sky === 'string' && /^#[0-9a-f]{6}$/i.test(p.sky) ? p.sky : DEFAULT_LIGHT.sky,
      /* ⚠️ Anyone with saved settings from before the clock existed has no
         `followClock` at all, and defaulting a missing boolean to `false` would
         quietly leave every existing player on the old frozen sun. */
      followClock: typeof p.followClock === 'boolean' ? p.followClock : DEFAULT_LIGHT.followClock,
      nightFloor: num(p.nightFloor, DEFAULT_LIGHT.nightFloor),
      clearSky: typeof p.clearSky === 'boolean' ? p.clearSky : DEFAULT_LIGHT.clearSky,
      forceRain: typeof p.forceRain === 'boolean' ? p.forceRain : DEFAULT_LIGHT.forceRain,
      holdItem: typeof p.holdItem === 'string' ? p.holdItem : DEFAULT_LIGHT.holdItem,
      preset:
        p.preset === 'day' || p.preset === 'night' || p.preset === 'none'
          ? p.preset
          : DEFAULT_LIGHT.preset,
    };
  } catch {
    return DEFAULT_LIGHT;
  }
}

/**
 * The two settings that decide whether distant ground shimmers (T84).
 *
 * ⚠️ **Both exist because haugur saw the same build look fine on an M4 MacBook
 * and terrible on an RTX 3050**, which is the opposite way round from what
 * anyone would guess. The cause is not the GPU:
 *
 * 1. **three refuses anisotropic filtering unless every filter mode is
 *    linear** — verified in `three.webgpu.js`, in the sampler it builds:
 *    *"anisotropy can only be used when all filter modes are set to linear"*.
 *    Asking for crisp Minecraft pixels up close therefore turns off the one
 *    thing that stops ground crawling when you look across it. In WebGPU it is
 *    one sampler and one choice; Minecraft gets both because GL let you set
 *    them separately.
 * 2. **The MacBook has a Retina screen**, so `devicePixelRatio` is 2 and it
 *    renders at double resolution and scales down. That is free antialiasing,
 *    and it was hiding the problem rather than not having it.
 *
 * So rather than pick haugur's trade-off for him on a machine nobody can see,
 * both are switches — the same argument as the light sliders.
 */
export interface GraphicsSettings {
  /** Nearest-neighbour magnification: crisp pixels, but no anisotropy. */
  readonly crisp: boolean;
  /**
   * Multiplier on the device pixel ratio.
   *
   * Above 1 is supersampling — render bigger, scale down — which is exactly
   * what the Retina Mac was doing for free. It costs frame rate roughly with
   * the square, so 1.5 is about 2.25× the pixels.
   *
   * ⚠️ **Measured 2026-08-07: taking this to 2 changes nothing on a Windows
   * machine reporting ratio 1** — which is the same number of pixels the Mac
   * draws. That is what rules simple under-sampling *out*, and it is why the
   * four switches below exist.
   */
  readonly renderScale: number;
  /** Draw the block tiles at all. Off leaves a flat mid grey. */
  readonly textures: boolean;
  /** Darken enclosed corners. */
  readonly ambientOcclusion: boolean;
  /** Give each block its own ±8% brightness. */
  readonly jitter: boolean;
  /** Fade the two above out with distance, where nothing filters them. */
  readonly detailFade: boolean;
  /**
   * 🌊 **Let the water move** — T207.
   *
   * 🔴 **A real setting, and also the way out if it goes wrong.** Every water
   * block in the world draws from one animated bank of tiles, so if the
   * movement reads badly on a machine nobody here can see, **this turns it off
   * without a deploy** and the sea goes back to exactly the still photograph it
   * was — the frames are a separate set of layers, so nothing is lost by not
   * looking at them.
   *
   * ⚠️ **Not a diagnostic dressed as a preference** (the mistake the four
   * *what is crawling* switches were labelled to avoid): this one is a
   * preference, and somebody who dislikes moving water may leave it off.
   */
  readonly movingWater: boolean;
  /**
   * 🌊 **How solid the water looks, 0.15…1** — T158, D115.
   *
   * 1 is the opaque sea this game has always had; lower lets you see the bed,
   * and the fish, through it.
   *
   * 🔴 **A setting because nobody building this can see it.** That is the
   * reasoning the four *what is crawling* switches were built on, applied to
   * the one question only haugur can answer — *how clear should the sea be?*
   * It drives a shader uniform, so it needs no rebuild and no deploy, and he
   * can find the number on the machine that shows it.
   *
   * ⚠️ **Floored at 0.15 rather than 0.** Water you cannot see at all is not
   * clear water, it is a sea that has vanished — and a control that can make
   * the world look broken is one somebody sets wrong and never revisits.
   */
  readonly waterOpacity: number;
  /**
   * Sample one fixed mip level, or `null` for the ordinary automatic choice.
   *
   * ⚠️ **The decisive experiment.** Level 4 of a 16×16 tile is a single pixel —
   * one flat colour, no detail left to alias. If the distant ground still
   * crawls with that forced, the tile texture is not what is crawling and every
   * mipmap theory is dead.
   */
  readonly mipLevel: number | null;
}

export const DEFAULT_GRAPHICS: GraphicsSettings = {
  /**
   * 🔴 **CRISP, and haugur settled it by looking at both — 2026-08-13.**
   *
   * 🎯 *"crisp looks better."* ⚠️ **He said the opposite first and corrected it
   * within the hour**, so the value here is unchanged from what it always was —
   * but it is no longer a default nobody had chosen. **That distinction is the
   * point of this comment.**
   *
   * ⚠️ **The note above deliberately left this open** — *"rather than pick
   * haugur's trade-off for him on a machine nobody can see."* **He has now seen
   * it, on his own hardware, which is the only place the question could be
   * answered.** The workstation this is written on has no GPU and renders in
   * software, so nothing here could have decided it.
   *
   * ## ⚠️ What choosing crisp costs, stated plainly so nobody rediscovers it
   *
   * **three enables anisotropic filtering only when every filter mode is
   * linear**, so nearest-neighbour magnification turns it off for the whole
   * scene — and the T84 note above names exactly that as the reason **distant
   * ground crawls**. 🔴 **So the crawl is a price, not a bug to be hunted.**
   *
   * 👉 **The ways to buy it back without giving up hard pixels**, in order of
   * cheapness: **`renderScale` above 1** (supersampling — what the Retina Mac
   * was doing for free), and **finer tiles**, since a 64 px texture puts four
   * times more detail in the same metre before any filtering happens.
   *
   * 🔴 **Do not flip this to `false` to fix the crawl.** It was tried on
   * 2026-08-13, it is not what he wants the game to look like, and
   * `graphics.test.ts` now says so.
   */
  crisp: true,
  renderScale: 1,
  textures: true,
  ambientOcclusion: true,
  jitter: true,
  detailFade: true,
  /* 🌊 On. haugur asked for the rivers to move; a feature that ships switched
     off is a feature nobody sees. */
  movingWater: true,
  /**
   * 🌊 **0.35 — and it is haugur's number, not a guess.** T158, T220, D115.
   *
   * 🔴 **It shipped at 0.72 this morning and was WRONG BY THE AFTERNOON, for a
   * reason that could not be seen until there were fish.** He approved 0.72
   * looking at an empty sea; the same day, a sheet of five þorskur at true size
   * six metres out showed that **at 0.72 a fish under the surface is a faint
   * grey ghost.**
   *
   * ⚠️ **Making the FISH paler changed almost nothing** — three skins were
   * baked, from dark olive to near-white, and the water was what hid them.
   *
   * 🎯 **haugur, 2026-09-16, shown 72% and 35% side by side: "35%".**
   */
  waterOpacity: 0.35,
  mipLevel: null,
};

const GRAPHICS_KEY = 'lofi.graphics';

function loadGraphics(): GraphicsSettings {
  try {
    const raw = window.localStorage.getItem(GRAPHICS_KEY);
    if (raw === null) return DEFAULT_GRAPHICS;
    const p: unknown = JSON.parse(raw);
    if (typeof p !== 'object' || p === null) return DEFAULT_GRAPHICS;
    const g = p as Partial<Record<keyof GraphicsSettings, unknown>>;
    const flag = (v: unknown, fallback: boolean): boolean =>
      typeof v === 'boolean' ? v : fallback;
    return {
      crisp: flag(g.crisp, DEFAULT_GRAPHICS.crisp),
      renderScale:
        typeof g.renderScale === 'number' && Number.isFinite(g.renderScale)
          ? Math.min(2, Math.max(0.5, g.renderScale))
          : DEFAULT_GRAPHICS.renderScale,
      textures: flag(g.textures, DEFAULT_GRAPHICS.textures),
      ambientOcclusion: flag(g.ambientOcclusion, DEFAULT_GRAPHICS.ambientOcclusion),
      jitter: flag(g.jitter, DEFAULT_GRAPHICS.jitter),
      detailFade: flag(g.detailFade, DEFAULT_GRAPHICS.detailFade),
      movingWater: flag(g.movingWater, DEFAULT_GRAPHICS.movingWater),
      /* 🌊 Clamped on the way IN, not just at the slider — a hand-edited or
         half-written stored value must not be able to make the sea vanish. */
      waterOpacity:
        typeof g.waterOpacity === 'number' && Number.isFinite(g.waterOpacity)
          ? Math.min(WATER_OPACITY_MAX, Math.max(WATER_OPACITY_MIN, g.waterOpacity))
          : DEFAULT_GRAPHICS.waterOpacity,
      /* ⚠️ A stored forced mip level is a diagnostic, never a look. It is
         deliberately NOT restored — coming back to a world pinned to one flat
         mip weeks later would look like a new bug. */
      mipLevel: DEFAULT_GRAPHICS.mipLevel,
    };
  } catch {
    return DEFAULT_GRAPHICS;
  }
}

/**
 * 🌊 **The clearest and the most solid the water may be** — T158, D115.
 *
 * 🔴 **The floor is not 0.** Water you cannot see at all is a sea that has
 * vanished, not clear water, and a control that can make the world look broken
 * is one somebody sets wrong and never revisits. **1 is the opaque sea this
 * game shipped with**, so the old look is always one drag away.
 */
export const WATER_OPACITY_MIN = 0.15;
export const WATER_OPACITY_MAX = 1;

/** The narrowest and widest view the slider offers. See {@link MenuSettings}. */
export const FOV_MIN = 60;
export const FOV_MAX = 110;

/**
 * 👁️ How wide the view is, in degrees (T187).
 *
 * 🎯 **haugur, 2026-09-11:** *"POV slider"* — the first thing he named on the
 * Main tab.
 *
 * ⚠️ **75 is what the game has always drawn** and it stays the default:
 * `main.ts` built its `PerspectiveCamera` with a hard-coded 75 from the first
 * commit, so anybody who has played this game has played it at 75. Changing the
 * default while adding the slider would mean every returning player's world
 * quietly changed shape, and they would have no way to know it was us.
 *
 * 📖 **The range is Minecraft's, narrowed at the bottom.** Minecraft's own
 * slider runs **30–110 with a default of 70**, and 110 is what it labels *Quake
 * Pro*. 🔴 **We do not go below 60.** Minecraft's 30 is a telephoto lens: it
 * looks like the world is being watched through a tube, it makes the hand
 * enormous, and no reading of "field of view" that a player wants lives down
 * there. ⚠️ **This bottom stop is a judgement made by arithmetic and not by
 * eye** — this workstation has no GPU and cannot see the client — so it is
 * haugur's to confirm or move.
 */
export interface MenuSettings {
  /** Vertical field of view, degrees. */
  readonly fov: number;
  /**
   * 🔊 **Nothing in this game makes a sound yet** — verified 2026-09-11 by
   * grepping the whole repository for `AudioContext`, `new Audio`, any `.mp3`
   * or `.ogg`, and every audio library. There were no hits anywhere.
   *
   * 🎯 **haugur chose to build the tab now anyway**, with the values saved and
   * waiting, so the day sound exists it already knows how loud to be.
   *
   * 🔴 **The tab must say so on its own face, and `gamemenu.ts` does.** A
   * volume slider that silently controls silence is exactly the shape of fault
   * this project keeps paying for — a control that describes something the game
   * cannot do reads as working and is never re-checked. ⚠️ **Delete that notice
   * in the same session the first sound plays**, or it becomes the lie pointing
   * the other way.
   */
  readonly masterVolume: number;
  readonly musicOn: boolean;
  readonly effectsOn: boolean;
}

export const DEFAULT_MENU: MenuSettings = {
  /* ⚠️ Unchanged from the hard-coded camera it replaces. See above. */
  fov: 75,
  masterVolume: 0.7,
  musicOn: true,
  effectsOn: true,
};

const MENU_KEY = 'lofi.menu.v1';

function loadMenu(): MenuSettings {
  try {
    const raw = window.localStorage.getItem(MENU_KEY);
    if (raw === null) return DEFAULT_MENU;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return DEFAULT_MENU;
    const p = parsed as Partial<Record<keyof MenuSettings, unknown>>;
    return {
      /* ⚠️ Clamped on the way in, not merely type-checked. A hand-edited or
         stale `localStorage` carrying `fov: 3` would give a camera nobody can
         play with and no clue where it came from. */
      fov:
        typeof p.fov === 'number' && Number.isFinite(p.fov)
          ? Math.min(FOV_MAX, Math.max(FOV_MIN, p.fov))
          : DEFAULT_MENU.fov,
      masterVolume:
        typeof p.masterVolume === 'number' && Number.isFinite(p.masterVolume)
          ? Math.min(1, Math.max(0, p.masterVolume))
          : DEFAULT_MENU.masterVolume,
      musicOn: typeof p.musicOn === 'boolean' ? p.musicOn : DEFAULT_MENU.musicOn,
      effectsOn: typeof p.effectsOn === 'boolean' ? p.effectsOn : DEFAULT_MENU.effectsOn,
    };
  } catch {
    return DEFAULT_MENU;
  }
}

/**
 * Where the render distance is remembered. ⚠️ **The key is unchanged from when
 * this lived in `main.ts`** — see {@link viewRadiusStore}.
 */
const VIEW_RADIUS_KEY = 'lofi.viewRadius';

/** The stored distance, or what `?radius=` asks for, or the shipped default. */
function readViewRadius(): number {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('radius');
    if (fromUrl !== null && fromUrl !== '') return clampViewRadius(Number(fromUrl));

    const stored = window.localStorage.getItem(VIEW_RADIUS_KEY);
    return stored === null ? DEFAULT_VIEW_RADIUS : clampViewRadius(Number(stored));
  } catch {
    return DEFAULT_VIEW_RADIUS;
  }
}

/**
 * One setting, one save, many readers.
 *
 * ⚠️ **`set` replaces the whole value rather than merging a patch.** Every
 * caller in this client already builds `{ ...current, one: change }`, and a
 * merge would quietly accept a partial object that dropped a field.
 */
class Store<T> {
  private current: T;
  private readonly key: string;
  private readonly listeners = new Set<(value: T) => void>();

  /* ⚠️ Both fields are assigned by hand rather than declared as constructor
     parameter properties: this repo builds with `erasableSyntaxOnly`, so
     `constructor(private readonly key: string)` is a compile error. */
  constructor(key: string, initial: T) {
    this.key = key;
    this.current = initial;
  }

  get(): T {
    return this.current;
  }

  set(next: T): void {
    this.current = next;
    try {
      window.localStorage.setItem(this.key, JSON.stringify(next));
    } catch {
      /* Private browsing, or a full quota. Losing the setting on reload is not
         worth failing the render for — the same call this file's predecessor
         made, kept deliberately. */
    }
    for (const listener of this.listeners) listener(next);
  }

  /**
   * Be told when this changes — **and be told once, now.**
   *
   * See the note at the top of this file: a window that renders itself only on
   * change is wrong on the frame it opens. Returns an unsubscribe function.
   */
  subscribe(listener: (value: T) => void): () => void {
    this.listeners.add(listener);
    listener(this.current);
    return () => this.listeners.delete(listener);
  }
}

/** 🌞 The sky and the light. Shared by the game menu and Dev Tools. */
export const lightStore = new Store<LightSettings>(LIGHT_KEY, loadLight());

/** 👁️🔊 Field of view, and the audio values that are waiting for audio. */
export const menuStore = new Store<MenuSettings>(MENU_KEY, loadMenu());

/** 🖼️ Texture filtering, render scale and the T94 isolation switches. */
export const graphicsStore = new Store<GraphicsSettings>(GRAPHICS_KEY, loadGraphics());

/**
 * 👀 **How far you can see, in chunks** (T188).
 *
 * 🔴 **It kept its own key, `lofi.viewRadius`, rather than joining
 * `GraphicsSettings`.** Folding it in would have changed the shape of a stored
 * object that every player already has, and the only gain would have been one
 * fewer line here — so anybody who has tuned their distance keeps it.
 *
 * ⚠️ **`?radius=` on the URL still wins**, and is read once at startup. It is
 * how `shoot.mjs` and the probes ask for a small world without touching the
 * player's own setting.
 *
 * 🔴 **Changing this is not free and that is the whole point of exposing it.**
 * A radius of 8 is ~788 chunks in view; every step out is roughly the square.
 * 🎯 **A tester, 2026-09-10:** *"game runs laggy"* — and this is the first
 * thing to try, which is exactly why it should not have been a keypress nobody
 * knew about.
 */
export const viewRadiusStore = new Store<number>(VIEW_RADIUS_KEY, readViewRadius());
